/**
 * "Ask Resto" — the deterministic EN/RO concierge parser.
 *
 * Turns a sentence like "pizza deschis acum cu reducere" into the feed's own
 * filter vocabulary: taxonomy tokens (chips the visitor can edit or remove) +
 * scalar intents (open now, capacity, distance, offers, fast venues). Pure,
 * offline, and keyless — no server, no model, no I/O. A later iteration sends
 * hard queries to `/api/ai/parse-search`; when that call fails, times out, or
 * isn't configured, this file is what answers, so it must always stand alone.
 *
 * Matching order is load-bearing:
 *   1. Taxonomy labels/slugs first, longest phrase first, each match spliced
 *      out of the remaining text. Precise vocabulary wins — "fast food" is the
 *      cuisine, "quick bite" is the tag, and "vegetarian friendly" beats the
 *      bare "vegetarian" cuisine because it's the longer phrase.
 *   2. The intent lexicon over what's left, same longest-first splice. Loose
 *      words the taxonomy has no term for ("quick", "takeaway", "liber") land
 *      here.
 *   3. Whatever survives is free text and goes back into the search box.
 *
 * Nothing here throws: an unrecognized query round-trips unchanged with
 * `matched: false`, and the caller just keeps the plain text search.
 */

import { foldText, type FilterToken, type IntentFilters } from "@/lib/feedFilter";
import type { Cuisine, Tag } from "@/types/taxonomy";

export interface ParsedQuery {
  /** Scalar dimensions the sentence asked for. */
  intent: IntentFilters;
  /** Cuisine/tag chips the sentence named, in match order, deduped. */
  tokens: FilterToken[];
  /** Leftover free text for the search box ("" when fully consumed). */
  text: string;
  /** Did we recognize anything at all? `false` ⇒ leave the feed alone. */
  matched: boolean;
}

export interface Vocabulary {
  cuisines: Cuisine[];
  tags: Tag[];
}

/** "near me" radius — a comfortable walk, not a drive across town. */
export const NEAR_ME_KM = 2;

/** Fold + strip punctuation so every comparison is space-delimited words. */
function normalize(s: string): string {
  return foldText(s)
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Words that carry no filtering meaning on their own. Only ever applied to
 * what's LEFT after matching, so a venue actually named "La Pescar" is safe —
 * "la" is dropped from leftovers, never from a matched phrase.
 */
const STOPWORDS = new Set([
  // en — grammar
  "a", "an", "the", "with", "and", "or", "some", "me", "i", "want", "to", "for",
  "in", "at", "on", "is", "are", "what", "whats", "show", "find", "please", "any",
  // en — fillers that mean "a venue". Left in, they'd become a text search no
  // venue can satisfy and silently empty the feed after an ask.
  "somewhere", "anywhere", "something", "anything", "place", "places", "spot",
  "spots", "restaurant", "restaurants", "venue", "venues", "food", "eat",
  // ro — grammar
  "cu", "si", "sau", "un", "o", "la", "in", "de", "pe", "ceva", "vreau", "caut",
  "imi", "mi", "va", "care", "e", "este", "sunt", "vreo", "niste",
  // ro — fillers
  "undeva", "oriunde", "loc", "locuri", "local", "localuri", "restaurant",
  "restaurante", "mananc", "mancare",
]);

type Apply = (intent: IntentFilters) => void;

/**
 * Intent lexicon — folded, ASCII, space-delimited. Deliberately does NOT
 * contain phrases the taxonomy already owns ("fast food", "quick bite",
 * "rapid"): those resolve to real chips in step 1 instead.
 *
 * Negations are spelled out as their own phrases ("fara aglomeratie", "nu e
 * aglomerat") rather than handled generally — a rule-based floor gets that
 * wrong more often than it gets it right.
 */
const LEXICON: { phrase: string; apply: Apply }[] = [
  // -- open now -------------------------------------------------------------
  // Romanian agrees in gender/number, so the forms a diner actually types
  // ("local deschis", "terasă deschisă") each need their own entry.
  ...["open now", "open right now", "whats open", "what is open", "still open", "open",
      "deschis acum", "deschisa acum", "deschise acum", "inca deschis",
      "deschis", "deschisa", "deschise"].map(
    (phrase) => ({ phrase, apply: ((i) => { i.openNow = true; }) as Apply }),
  ),
  // -- a running offer ------------------------------------------------------
  ...["discount", "discounts", "discounted", "deal", "deals", "offer", "offers",
      "promo", "promotion", "happy hour",
      "reducere", "reduceri", "oferta", "oferte", "promotie", "promotii"].map(
    (phrase) => ({ phrase, apply: ((i) => { i.hasDiscount = true; }) as Apply }),
  ),
  // -- fast-venue tier (§28) ------------------------------------------------
  ...["quick", "quickly", "takeaway", "take away", "to go", "grab a bite", "grab",
      "repede", "la pachet", "in graba"].map(
    (phrase) => ({ phrase, apply: ((i) => { i.fastVenue = true; }) as Apply }),
  ),
  // -- capacity: room to spare ---------------------------------------------
  ...["not crowded", "not busy", "no crowds", "wide open", "quiet", "empty",
      "free table", "free tables", "with space", "has space",
      "fara aglomeratie", "fara aglomeratii", "nu e aglomerat", "nu aglomerat",
      "nu e aglomerata", "locuri libere", "liber", "libera", "libere",
      "gol", "goala", "linistit", "linistita", "linistite"].map(
    (phrase) => ({ phrase, apply: ((i) => { i.capacityMax = "GREEN"; }) as Apply }),
  ),
  // -- capacity: just not packed -------------------------------------------
  ...["not packed", "not full", "not too busy", "some space",
      "nu prea aglomerat", "nu prea aglomerata", "nu foarte aglomerat",
      "nu e plin", "nu e plina"].map(
    (phrase) => ({ phrase, apply: ((i) => { i.capacityMax = "YELLOW"; }) as Apply }),
  ),
  // -- walking distance -----------------------------------------------------
  ...["near me", "nearby", "close to me", "close by", "walking distance",
      "around here", "near",
      "aproape de mine", "in apropiere", "prin apropiere", "langa mine",
      "pe jos", "aproape"].map(
    (phrase) => ({ phrase, apply: ((i) => { i.maxDistanceKm = NEAR_ME_KM; }) as Apply }),
  ),
].sort(byPhraseLength);

function byPhraseLength(
  a: { phrase: string; rank?: number },
  b: { phrase: string; rank?: number },
): number {
  const wordsA = a.phrase.split(" ").length;
  const wordsB = b.phrase.split(" ").length;
  return (
    wordsB - wordsA ||
    b.phrase.length - a.phrase.length ||
    // Equal-length tie: a term's own label beats a fragment split off someone
    // else's — "brunch" is the Brunch tag, not half of "Café & Brunch".
    (a.rank ?? 0) - (b.rank ?? 0)
  );
}

/**
 * A cursor over the query that can consume whole-word phrases. Kept as a
 * padded, space-delimited string so `" pizza "` never matches inside
 * `"pizzeria"` and a taxonomy tag named "bar" never eats "barbecue".
 */
function makeCursor(normalized: string) {
  let rest = ` ${normalized} `;
  return {
    take(phrase: string): boolean {
      const needle = ` ${phrase} `;
      const at = rest.indexOf(needle);
      if (at === -1) return false;
      rest = `${rest.slice(0, at)} ${rest.slice(at + needle.length)}`;
      return true;
    },
    has(phrase: string): boolean {
      return rest.includes(` ${phrase} `);
    },
    /** What's left, minus stopwords. */
    leftover(): string {
      return rest
        .split(" ")
        .filter((w) => w !== "" && !STOPWORDS.has(w))
        .join(" ");
    },
  };
}

/** Every phrase that should resolve to a taxonomy chip, longest first. */
function taxonomyPhrases(vocab: Vocabulary): { token: FilterToken; phrase: string; rank: number }[] {
  const out: { token: FilterToken; phrase: string; rank: number }[] = [];
  const seen = new Set<string>();

  const add = (token: FilterToken, raw: string, rank: number) => {
    const phrase = normalize(raw);
    // 1-char fragments (the "&" halves of a label, stray letters) would match
    // half the alphabet; taxonomy terms are words.
    if (phrase.length < 3) return;
    const key = `${token.kind}:${token.slug}:${phrase}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ token, phrase, rank });
  };

  const addAll = (token: FilterToken, labels: string[]) => {
    for (const label of labels) {
      add(token, label, 0);
      // "Japanese & Sushi" also answers to "japanese" and to "sushi"; "Café &
      // Brunch" to "brunch". Split on the joiners operators actually type —
      // rank 1, so a term whose own label IS that word still wins the tie.
      if (/[&/]/.test(label)) for (const part of label.split(/[&/]/)) add(token, part, 1);
    }
  };

  for (const c of vocab.cuisines) {
    addAll({ kind: "cuisine", slug: c.slug }, [c.label_en, c.label_ro, c.slug]);
  }
  for (const tg of vocab.tags) {
    addAll({ kind: "tag", slug: tg.slug }, [tg.label_en, tg.label_ro, tg.slug]);
  }
  return out.sort(byPhraseLength);
}

/**
 * Parse a natural-language query against the live taxonomy. Deterministic:
 * same query + same vocabulary → same result, every time.
 */
export function parseNaturalQuery(query: string, vocab: Vocabulary): ParsedQuery {
  const normalized = normalize(query);
  if (!normalized) return { intent: {}, tokens: [], text: "", matched: false };

  const cursor = makeCursor(normalized);
  const tokens: FilterToken[] = [];
  const taken = new Set<string>();

  // 1. Taxonomy first — precise vocabulary beats loose intent words.
  for (const { token, phrase } of taxonomyPhrases(vocab)) {
    const key = `${token.kind}:${token.slug}`;
    if (taken.has(key)) continue;
    if (cursor.take(phrase)) {
      taken.add(key);
      tokens.push(token);
    }
  }

  // 2. Intent lexicon over what's left.
  const intent: IntentFilters = {};
  for (const { phrase, apply } of LEXICON) {
    if (cursor.take(phrase)) apply(intent);
  }

  const text = cursor.leftover();
  const matched = tokens.length > 0 || Object.keys(intent).length > 0;
  // Nothing recognized → hand the original query back untouched, so the caller
  // can leave the search box exactly as the visitor typed it.
  return matched ? { intent, tokens, text, matched } : { intent: {}, tokens: [], text: query, matched: false };
}

/**
 * Is this query worth offering the "Ask Resto" pill for? A sentence (≥3 words)
 * or anything carrying a known intent word — a bare "pizza" is already served
 * fine by the plain search.
 */
export function looksAskable(query: string): boolean {
  const normalized = normalize(query);
  if (!normalized) return false;
  if (normalized.split(" ").length >= 3) return true;
  const cursor = makeCursor(normalized);
  return LEXICON.some(({ phrase }) => cursor.has(phrase));
}
